/**
 * @file creditReallocation.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description Editing a customer credit note that is ALLOCATED (Cam, 15 Oct
 *              2026: "credit notes should be editable even once allocated, so
 *              long as created in the current month and not the previous
 *              month which is behind the Xero accounting lock"). The pure half
 *              of services/creditReallocateService.js.
 *
 *   Xero refuses any change to a credit note with an allocation against it.
 *   So the edit takes the allocations off in Xero, changes the credit (the
 *   posted-edit patch), then allocates it again to the same invoices in the
 *   same order — each up to what is left on the credit and what the invoice
 *   owes as Xero has it then. Never void-and-replace: the credit keeps its
 *   number, its links and its history.
 *
 *   WHICH CREDITS (allocatedEditBlock — null = it may be edited this way):
 *   a credit note made here (source 'local', transaction type CREDIT) whose
 *   used amount is ALLOCATIONS only — a cash refund still refuses, saying so
 *   — with posted editing switched on, every allocation dated after the Xero
 *   lock (the credit's own dates are checked by the posted edit as for any
 *   document). Not a till credit, not voided (editBlock refuses those first).
 *
 *   One rule for the screen (Create Invoice's notice before Save) and the
 *   server: the server's utils/creditReallocation.js is GENERATED from this
 *   file by scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { formatDate } from './dateFormatters';

const PENNY = 0.005;
export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (v) => `£${round2(Math.abs(num(v))).toFixed(2)}`;
const day = (v) => {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString().slice(0, 10);
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v));
  if (m) return m[1];
  const ms = /\/Date\((-?\d+)/.exec(String(v));
  if (ms) return new Date(parseInt(ms[1], 10)).toISOString().slice(0, 10);
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

const GENERIC = 'Part of this credit is allocated or refunded — remove that in Xero first, then edit it';

/**
 * Why a credit that has been used (amount_paid > 0) cannot be edited here, or
 * null when it can — by taking its allocations off and putting them back.
 *
 * @param {object} inv  invoices row (is_credit, transaction_type, source,
 *                      amount_paid) with `credit_alloc` = { allocated,
 *                      refunded, allocations: [{ invoice_no, amount, date }] }
 *                      from creditReallocateService.factsFor — absent = not
 *                      known, refused as before
 * @param {{ postedEdit?: boolean, lockedReason?: (date: string) => string|null }} [opts]
 */
export function allocatedEditBlock(inv, { postedEdit = false, lockedReason = () => null } = {}) {
  if (!inv || !inv.is_credit) return 'This invoice has payments against it — remove them in Xero first, then edit it';
  const tt = String(inv.transaction_type || 'CREDIT').toUpperCase();
  if (tt !== 'CREDIT' && tt !== 'ACCRECCREDIT') return GENERIC;
  if (inv.source !== 'local') return GENERIC;
  const facts = inv.credit_alloc;
  if (!facts) return GENERIC;
  if (num(facts.refunded) > PENNY) {
    return `${money(facts.refunded)} of this credit has been refunded — a refunded credit can't be changed here. Remove the refund in Xero first, then edit it`;
  }
  const allocations = Array.isArray(facts.allocations) ? facts.allocations : [];
  if (!allocations.length || num(facts.allocated) + PENNY < Math.abs(num(inv.amount_paid))) {
    return 'Part of this credit is used in a way this system cannot see (an allocation or refund not recorded here) — remove that in Xero first, then edit it';
  }
  if (!postedEdit) {
    return 'This credit note is allocated — changing it takes the allocation off in Xero and puts it back, which needs editing of posted documents switched on';
  }
  for (const a of allocations) {
    const locked = lockedReason(day(a.date));
    if (locked) {
      return `It is allocated to ${a.invoice_no || 'an invoice'} on ${formatDate(day(a.date))}, in a period Xero has locked, so the allocation can't be taken off to edit it`;
    }
  }
  return null;
}

/**
 * How much to allocate to the next invoice: what is left on the credit, no
 * more than the invoice owes (as Xero has it now) and, when putting the
 * original allocations back, no more than it had before.
 */
export function nextAmount({ left, amountDue, original = null, restore = false }) {
  let amount = Math.min(num(left), num(amountDue));
  if (restore && original !== null && original !== undefined) amount = Math.min(amount, num(original));
  amount = round2(amount);
  return amount > PENNY ? amount : 0;
}

/**
 * The date an allocation is made on: its original date, moved forward to the
 * later of the two documents' dates and the first day Xero has open.
 */
export function allocationDate({ original, creditDate, invoiceDate, firstAllowedDate }) {
  return [day(original), day(creditDate), day(invoiceDate), day(firstAllowedDate)]
    .filter(Boolean).sort().pop() || null;
}

/**
 * The sentence the edit screen shows before Save: what is allocated and what
 * saving will do. `newTotal` is the credit's total as on the screen.
 */
export function beforeSaveNotice({ creditNo, allocations = [], newTotal = null }) {
  const list = (allocations || []).map(a => `${a.invoice_no || 'an invoice'} (${money(a.amount)})`);
  if (!list.length) return null;
  const to = list.length > 1 ? `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}` : list[0];
  const upTo = newTotal !== null && newTotal !== undefined ? ` (up to ${money(newTotal)})` : '';
  return `${creditNo || 'This credit note'} is allocated to ${to}. Saving removes the allocation${list.length > 1 ? 's' : ''} in Xero, `
    + `changes the credit, then allocates it again${upTo}.`;
}

/**
 * What happened, in one paragraph for the person and the History note.
 *
 * @param {object} o
 * @param {string} o.creditNo
 * @param {Array}  o.removed   [{ invoice_no, amount }]
 * @param {Array}  o.made      [{ invoice_no, amount }]
 * @param {Array}  [o.skipped] [{ invoice_no, reason }]
 * @param {Array}  [o.failed]  [{ invoice_no, reason }]
 * @param {Array}  [o.unknown] [{ invoice_no }]
 * @param {number} [o.left]    what is left unallocated on the credit
 * @param {'after_edit'|'restore'} [o.mode]
 */
export function outcomeText({ creditNo, removed = [], made = [], skipped = [], failed = [], unknown = [], left = 0, mode = 'after_edit' }) {
  const parts = [];
  const list = (xs) => xs.map(x => `${money(x.amount)} to ${x.invoice_no || 'an invoice'}`).join(', ');
  if (removed.length) parts.push(`Allocation${removed.length > 1 ? 's' : ''} removed to edit ${creditNo}: ${list(removed)}.`);
  if (mode === 'restore') {
    if (made.length) parts.push(`Put back: ${list(made)}.`);
  } else if (made.length) {
    parts.push(`Re-allocated: ${list(made)}.`);
  }
  for (const s of skipped) parts.push(`Not re-allocated to ${s.invoice_no || 'an invoice'}: ${s.reason}.`);
  for (const f of failed) parts.push(`Xero would not allocate to ${f.invoice_no || 'an invoice'}: ${f.reason}.`);
  for (const u of unknown) parts.push(`Xero did not answer for ${u.invoice_no || 'an invoice'} — whether it was allocated is not known; check ${creditNo} in Xero before allocating it again.`);
  if (num(left) > PENNY) parts.push(`${money(left)} of ${creditNo} is left unallocated.`);
  return parts.join(' ');
}

